import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { seniorFeedbackApi } from '@/api/seniorFeedbackApi';
import type { AddSeniorReviewerRequest, UpdateSeniorFeedbackRequest } from '@/api/types/jobEnd.types';
import { invalidateJobEndPageOnConflict, jobEndKeys } from './useJobEnds';

export const seniorFeedbackKeys = {
  byJobEnd: (jobEndId: number) => ['seniorFeedback', jobEndId] as const,
  eligibleReviewers: (jobEndId: number) => ['eligibleSeniorReviewers', jobEndId] as const,
};

export function useSeniorFeedbacks(jobEndId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: seniorFeedbackKeys.byJobEnd(jobEndId),
    queryFn: () => seniorFeedbackApi.getByJobEnd(jobEndId),
    enabled: !!jobEndId && enabled,
  });
}

export function useEligibleSeniorReviewers(jobEndId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: seniorFeedbackKeys.eligibleReviewers(jobEndId),
    queryFn: () => seniorFeedbackApi.getEligibleReviewers(jobEndId),
    enabled: !!jobEndId && enabled,
  });
}

export function useAddSeniorReviewer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobEndId, request }: { jobEndId: number; request: AddSeniorReviewerRequest }) =>
      seniorFeedbackApi.addReviewer(jobEndId, request),
    onError: (error, { jobEndId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, jobEndId);
      }
    },
  });
}

export function useUpdateSeniorFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ feedbackId, request, rowVersion }: { feedbackId: number; request: UpdateSeniorFeedbackRequest; rowVersion?: string; jobEndId: number }) =>
      seniorFeedbackApi.updateFeedback(feedbackId, request, rowVersion),
    onSuccess: (_, { jobEndId }) => {
      queryClient.invalidateQueries({ queryKey: jobEndKeys.detail(String(jobEndId)) });
    },
    onError: (error, { jobEndId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, jobEndId);
      }
    },
  });
}

export function useDeleteSeniorReviewer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ feedbackId }: { feedbackId: number; jobEndId: number }) =>
      seniorFeedbackApi.deleteReviewer(feedbackId),
    onError: (error, { jobEndId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, jobEndId);
      }
    },
  });
}

